import type { Container } from 'aurelia-framework';
import { Optional } from 'aurelia-framework';
import { EnvironmentService } from 'app/shared/services/environment-service';
import { CacheFactory } from './create-cache-token';

const DEFAULT_CACHE_TTL = 1000 * 60 * 3;
const LOCAL_CACHE_PROPERTY = 'cache';

export interface UseContainerCacheOpts {
    cacheFactory: CacheFactory<any>,
    cacheProp?: string;
}

/**
 * Create and set a cache on the views container for persisting data between view instances.
 * @example @UseContainerCache({ cacheFactory: () => createSimpleCache({ myValue: 'foo' })) })
 * @param opts
 * @constructor
 */
export function UseContainerCache(opts: UseContainerCacheOpts) {
    return function <TClass extends { new(...args: any[]): any }>(Target: TClass) {
        const created = Target.prototype.created as (Function | undefined);

        Target.prototype.created = function(...args: any[]) {
            const container = resolveContainer(this, args);
            const env = resolveEnv(container);
            const defaultTtl = env?.config.cacheDefaultTtl || DEFAULT_CACHE_TTL;
            const cacheProp = opts.cacheProp || LOCAL_CACHE_PROPERTY;

            // console.debug(`[Cache] Using ${env?.config.cacheDefaultTtl != null ? ' EssetteConfig' : ' build-in'} default TTL: ${defaultTtl}`);

            const setupCache = () => {
                if(!Object.getOwnPropertyDescriptor(Target.prototype, cacheProp)) {
                    // We need to lazily resolve the cache from the container due to Aurelia lifecycle timing.
                    Object.defineProperty(Target.prototype, cacheProp, {
                        configurable: true,
                        get: function() {
                            if (!this.cacheKey) {
                                // console.log(`[Cache] No cacheKey bound on view model; state will be bound to view instance only and not shared.`);
                                const cacheInstance = opts.cacheFactory({ defaultTtl });
                                Object.defineProperty(this, cacheProp, { value: cacheInstance, writable: true, configurable: true });
                                return cacheInstance;
                            }

                            const sharedInstance = container?.get(Optional.of(this.cacheKey));

                            if (sharedInstance) {
                                // console.debug(`[Cache] Retrieved shared cache instance from container for key ${this.cacheKey}.`);
                                Object.defineProperty(this, cacheProp, { value: sharedInstance, writable: true, configurable: true });
                                return sharedInstance;
                            }

                            // console.debug(`[Cache] No cache instance found in container for key ${this.cacheKey}; creating new instance and setting on container.`);
                            const newCacheInstance = opts.cacheFactory({ defaultTtl });
                            container?.registerInstance(this.cacheKey, newCacheInstance);
                            Object.defineProperty(this, cacheProp, { value: newCacheInstance, writable: true, configurable: true });
                            return newCacheInstance;

                        }
                    });
                }
            }

            setupCache();

            if(typeof created === 'function') {
                return created.apply(this, args);
            }
        };

        return Target;
    };
}

function resolveContainer(vm: any, createdArgs: any[]): Container {
    const [owningView, myView] = createdArgs || [];
    const c: Container | undefined =
        myView?.container ||
        owningView?.container ||
        vm.container ||
        (vm.view?.container);

    if(!vm.container) {
        vm.container = c;
    }

    if(!c) {
        throw new Error(`Cannot determine container for ${vm}. Ensure this utility is called in the created() lifecycle.`);
    }

    return c;
}

function resolveEnv(container: Container): EnvironmentService | undefined {
    // Prefer Optional to avoid hard failure if not registered
    return container.get(Optional.of<EnvironmentService>((EnvironmentService as any)));
}
